import { useState, useRef } from 'react';
import { 
  FaUpload, 
  FaSearch, 
  FaFilter, 
  FaEye, 
  FaGlobe,
  FaSpinner,
} from 'react-icons/fa';
import { 
  IoGridOutline,
  IoListOutline,
  IoClose,
  IoCheckboxOutline,
  IoCheckbox,
  IoImageOutline
} from 'react-icons/io5';
import { useServiceList } from '../../../hooks/useServiceList';

// Image converter utility functions
const convertToWebP = async (file, options = {}) => {
  const {
    quality = 0.85,
    maxWidth = 1920,
    maxHeight = 1920,
    generateThumbnail = true,
    thumbnailSize = 400
  } = options;

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = async () => {
        try {
          // Main image conversion
          const mainCanvas = document.createElement('canvas');
          const mainCtx = mainCanvas.getContext('2d');
          
          // Calculate dimensions maintaining aspect ratio
          let { width, height } = img;
          if (width > maxWidth || height > maxHeight) {
            const aspectRatio = width / height;
            if (width > height) {
              width = maxWidth;
              height = width / aspectRatio;
            } else {
              height = maxHeight;
              width = height * aspectRatio;
            }
          }
          
          mainCanvas.width = width;
          mainCanvas.height = height;
          mainCtx.drawImage(img, 0, 0, width, height);
          
          // Convert main image to WebP
          const mainBlob = await new Promise(resolve => {
            mainCanvas.toBlob(resolve, 'image/webp', quality);
          });
          
          const mainFile = new File(
            [mainBlob], 
            file.name.replace(/\.[^.]+$/, '.webp'),
            { type: 'image/webp' }
          );
          
          // Generate thumbnail if requested
          let thumbnailFile = null;
          if (generateThumbnail) {
            const thumbCanvas = document.createElement('canvas');
            const thumbCtx = thumbCanvas.getContext('2d');
            
            // Calculate thumbnail dimensions
            let thumbWidth = thumbnailSize;
            let thumbHeight = thumbnailSize;
            
            if (img.width > img.height) {
              thumbHeight = (thumbnailSize / img.width) * img.height;
            } else {
              thumbWidth = (thumbnailSize / img.height) * img.width;
            }
            
            thumbCanvas.width = thumbWidth;
            thumbCanvas.height = thumbHeight;
            thumbCtx.drawImage(img, 0, 0, thumbWidth, thumbHeight);
            
            const thumbBlob = await new Promise(resolve => {
              thumbCanvas.toBlob(resolve, 'image/webp', 0.7);
            });
            
            thumbnailFile = new File(
              [thumbBlob],
              file.name.replace(/\.[^.]+$/, '_thumb.webp'),
              { type: 'image/webp' }
            );
          }
          
          // Calculate compression stats
          const compressionRatio = ((file.size - mainBlob.size) / file.size * 100).toFixed(1);
          
          resolve({
            mainFile,
            thumbnailFile,
            originalSize: file.size,
            compressedSize: mainBlob.size,
            compressionRatio,
            dimensions: { width, height }
          });
        } catch (error) {
          reject(error);
        }
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
};

// Check if browser supports WebP
const supportsWebP = () => {
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = 1;
  return canvas.toDataURL('image/webp').indexOf('image/webp') === 0;
};

// Batch convert multiple files
const batchConvertToWebP = async (files, options = {}, onProgress) => {
  const results = [];
  const totalFiles = files.length;
  
  for (let i = 0; i < totalFiles; i++) {
    const file = files[i];
    
    // Skip if already WebP
    if (file.type === 'image/webp') {
      results.push({
        mainFile: file,
        skipped: true
      });
      continue;
    }
    
    // Skip if not an image
    if (!file.type.startsWith('image/')) {
      results.push({
        error: 'Not an image file',
        file
      });
      continue;
    }
    
    try {
      const converted = await convertToWebP(file, options);
      results.push(converted);
      
      if (onProgress) {
        onProgress({
          current: i + 1,
          total: totalFiles,
          percentage: ((i + 1) / totalFiles * 100).toFixed(0)
        });
      }
    } catch (error) {
      results.push({
        error: error.message,
        file
      });
    }
  }
  
  return results;
};

const GalleryManager = ({ 
  images, 
  categories, 
  loading, 
  uploading, 
  onUpload, 
  onUpdateImage, 
  onDeleteImages, 
  onToggleLandingPage,
  onBulkUpdate,
  filters,
  onUpdateFilters 
}) => {
  const [viewMode, setViewMode] = useState('grid');
  const [selectedImages, setSelectedImages] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showBulkActions, setShowBulkActions] = useState(false);
  const fileInputRef = useRef(null);

  // WebP conversion states
  const [conversionProgress, setConversionProgress] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [previewImages, setPreviewImages] = useState([]);
  const [showConversionSettings, setShowConversionSettings] = useState(false);
  const [conversionOptions, setConversionOptions] = useState({
    quality: 0.85,
    maxWidth: 1920,
    generateThumbnail: true
  });

  // Get services from Firestore
  const { services, loading: servicesLoading } = useServiceList();

  // Updated to match actual landing page structure
  const landingSections = [
    { id: 'hero', name: 'Hero Section', description: 'Single background image' },
    { id: 'about', name: 'About Section', description: 'Grid of 4 images' },
    { id: 'gallery', name: 'Gallery Section', description: 'Portfolio of 50 project images' }
  ];

  // Dynamic gallery categories from Firestore + Uncategorized
  const galleryCategories = [
    ...services.map(service => service.name),
    'Uncategorized'
  ];

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files && files.length > 0) {
      await processAndUploadFiles(files);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const files = Array.from(e.dataTransfer.files);
    if (files && files.length > 0) {
      await processAndUploadFiles(files);
    }
  };

  const processAndUploadFiles = async (files) => {
    if (!supportsWebP()) {
      // Fallback for browsers that don't support WebP
      onUpload(files);
      setShowUploadModal(false);
      return;
    }

    setConversionProgress({ current: 0, total: files.length, percentage: '0' });
    
    try {
      // Convert all files to WebP
      const results = await batchConvertToWebP(
        files, 
        conversionOptions,
        (progress) => setConversionProgress(progress)
      );
      
      // Filter out errors and prepare files for upload
      const successfulConversions = results.filter(r => !r.error);
      const filesToUpload = successfulConversions.map(r => r.mainFile);
      const thumbnails = successfulConversions
        .filter(r => r.thumbnailFile)
        .map(r => r.thumbnailFile);
      
      // Calculate total savings
      const totalOriginalSize = successfulConversions.reduce((sum, r) => sum + r.originalSize, 0);
      const totalCompressedSize = successfulConversions.reduce((sum, r) => sum + r.compressedSize, 0);
      const totalSavings = ((1 - totalCompressedSize / totalOriginalSize) * 100).toFixed(1);
      
      console.log(`Compression complete! Saved ${totalSavings}% overall`);
      
      // Upload main files and thumbnails
      onUpload([...filesToUpload, ...thumbnails]);
      
      // Reset state
      setShowUploadModal(false);
      setConversionProgress(null);
      setPreviewImages([]);
      
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Show success message (you might want to add a toast notification here)
      console.log(`Successfully converted ${filesToUpload.length} images to WebP`);
      
    } catch (error) {
      console.error('Conversion error:', error);
      setConversionProgress(null);
    }
  };

  const handleSelectImage = (imageId) => {
    setSelectedImages(prev => 
      prev.includes(imageId) 
        ? prev.filter(id => id !== imageId)
        : [...prev, imageId]
    );
  };

  const handleSelectAll = () => {
    if (selectedImages.length === images.length) {
      setSelectedImages([]);
    } else {
      setSelectedImages(images.map(img => img.id));
    }
  };

  const handleBulkAction = async (action, value = null) => {
    if (selectedImages.length === 0) return;

    switch (action) {
      case 'delete':
        await onDeleteImages(selectedImages);
        break;
      case 'activate':
        await onBulkUpdate(selectedImages, { isActive: true });
        break;
      case 'deactivate':
        await onBulkUpdate(selectedImages, { isActive: false });
        break;
      case 'category':
        await onBulkUpdate(selectedImages, { category: value });
        break;
      case 'landing-page':
        for (const imageId of selectedImages) {
          await onToggleLandingPage(imageId, value);
        }
        break;
      default:
        console.warn('Unknown bulk action:', action);
        return;
    }
    
    setSelectedImages([]);
    setShowBulkActions(false);
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (image) => {
    if (!image.isActive) {
      return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800">Inactive</span>;
    }
    if (image.onLandingPage) {
      const sectionName = landingSections.find(s => s.id === image.landingSection)?.name || 'Unknown';
      return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">On {sectionName}</span>;
    }
    return <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">Active</span>;
  };

  if (loading || servicesLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <FaSpinner className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading gallery...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Gallery Manager</h2>
          <p className="text-gray-600">Manage your gallery images and landing page assignments</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => setShowUploadModal(true)}
            className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            <FaUpload className="w-4 h-4" />
            Upload Images
          </button>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-md border transition-colors ${
              showFilters ? 'bg-gray-100 border-gray-300' : 'bg-white border-gray-300 hover:bg-gray-50'
            }`}
          >
            <FaFilter className="w-4 h-4" />
            Filters
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <IoImageOutline className="w-5 h-5 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Total Images</p>
              <p className="text-lg font-semibold text-gray-900">{images.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <FaEye className="w-5 h-5 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Active</p>
              <p className="text-lg font-semibold text-gray-900">
                {images.filter(img => img.isActive).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <FaGlobe className="w-5 h-5 text-yellow-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">On Landing Page</p>
              <p className="text-lg font-semibold text-gray-900">
                {images.filter(img => img.onLandingPage).length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white p-4 rounded-lg border">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <FaUpload className="w-5 h-5 text-purple-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Categories</p>
              <p className="text-lg font-semibold text-gray-900">{galleryCategories.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white p-4 rounded-lg border space-y-4">
        {/* Search and View Controls */}
        <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center">
          <div className="relative flex-1 max-w-md">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Search images..."
              value={filters.search}
              onChange={(e) => onUpdateFilters({ search: e.target.value })}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-md ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <IoGridOutline className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 rounded-md ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
            >
              <IoListOutline className="w-5 h-5" />
            </button>
            
            <div className="ml-4 flex items-center gap-2">
              <button
                onClick={() => setShowBulkActions(!showBulkActions)}
                className={`px-3 py-2 text-sm rounded-md border transition-colors ${
                  selectedImages.length > 0 
                    ? 'bg-blue-600 text-white border-blue-600 hover:bg-blue-700' 
                    : 'bg-white text-gray-600 border-gray-300'
                }`}
              >
                Bulk Actions ({selectedImages.length})
              </button>
              
              <button
                onClick={handleSelectAll}
                className="p-2 text-gray-600 hover:bg-gray-100 rounded-md"
                title={selectedImages.length === images.length ? 'Deselect all' : 'Select all'}
              >
                {selectedImages.length === images.length ? (
                  <IoCheckbox className="w-5 h-5" />
                ) : (
                  <IoCheckboxOutline className="w-5 h-5" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="border-t pt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={filters.category}
                  onChange={(e) => onUpdateFilters({ category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Categories</option>
                  {galleryCategories.map(cat => (
                    <option key={cat} value={cat.toLowerCase().replace(' ', '-')}>{cat}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={filters.status}
                  onChange={(e) => onUpdateFilters({ status: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Landing Page</label>
                <select
                  value={filters.landingPage}
                  onChange={(e) => onUpdateFilters({ landingPage: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Images</option>
                  <option value="assigned">On Landing Page</option>
                  <option value="unassigned">Not on Landing Page</option>
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={() => onUpdateFilters({ category: 'all', status: 'all', landingPage: 'all', search: '' })}
                  className="w-full px-3 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Actions */}
        {showBulkActions && selectedImages.length > 0 && (
          <div className="mt-4 p-4 bg-blue-50 rounded-lg">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleBulkAction('activate')}
                className="px-3 py-1 text-sm bg-green-100 text-green-700 rounded-md hover:bg-green-200 transition-colors"
              >
                Activate
              </button>
              <button
                onClick={() => handleBulkAction('deactivate')}
                className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
              >
                Deactivate
              </button>
              <select
                onChange={(e) => e.target.value && handleBulkAction('category', e.target.value)}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md"
                defaultValue=""
              >
                <option value="">Change Category</option>
                {galleryCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
              <select
                onChange={(e) => e.target.value && handleBulkAction('landing-page', e.target.value)}
                className="px-3 py-1 text-sm border border-gray-300 rounded-md"
                defaultValue=""
              >
                <option value="">Assign to Section</option>
                {landingSections.map(section => (
                  <option key={section.id} value={section.id}>{section.name}</option>
                ))}
              </select>
              <button
                onClick={() => handleBulkAction('delete')}
                className="px-3 py-1 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Images Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {images.map((image) => (
            <div key={image.id} className="bg-white rounded-lg shadow hover:shadow-md transition-shadow">
              <div className="relative">
                <img
                  src={image.thumbnail || image.url}
                  alt={image.alt || image.title}
                  className="w-full h-48 object-cover rounded-t-lg"
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-50 transition-all rounded-t-lg flex items-center justify-center opacity-0 hover:opacity-100">
                  <div className="flex gap-2">
                    <button
                      onClick={() => setSelectedImage(image)}
                      className="p-2 bg-white rounded-full text-gray-800 hover:bg-gray-100"
                      title="View details"
                    >
                      <FaEye className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => onToggleLandingPage(image.id)}
                      className={`p-2 rounded-full ${image.onLandingPage 
                        ? 'bg-green-600 text-white hover:bg-green-700' 
                        : 'bg-white text-gray-800 hover:bg-gray-100'
                      }`}
                      title={image.onLandingPage ? 'Remove from landing page' : 'Add to landing page'}
                    >
                      <FaGlobe className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Selection checkbox */}
                <div className="absolute top-2 left-2">
                  <button
                    onClick={() => handleSelectImage(image.id)}
                    className="p-1 bg-white bg-opacity-80 rounded"
                  >
                    {selectedImages.includes(image.id) ? (
                      <IoCheckbox className="w-4 h-4 text-blue-600" />
                    ) : (
                      <IoCheckboxOutline className="w-4 h-4 text-gray-600" />
                    )}
                  </button>
                </div>

                {/* Status badge */}
                <div className="absolute top-2 right-2">
                  {getStatusBadge(image)}
                </div>
              </div>

              <div className="p-4">
                <h3 className="font-medium text-gray-900 truncate">{image.title}</h3>
                <p className="text-sm text-gray-600 mt-1">{image.category || 'Uncategorized'}</p>
                
                {/* Display First checkbox */}
                <label className="flex items-center gap-2 text-sm mt-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={image.displayFirst || false}
                    onChange={(e) => onUpdateImage(image.id, { displayFirst: e.target.checked })}
                    className="rounded text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-gray-700">Display First</span>
                </label>
                
                <div className="flex justify-between items-center mt-2 text-xs text-gray-500">
                  <span>{formatDate(image.uploadedAt)}</span>
                  <span>{image.size}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        // List view implementation here...
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* List view content */}
        </div>
      )}

      {/* Upload Modal with WebP Conversion */}
      {showUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Upload Images</h3>
              <button
                onClick={() => {
                  setShowUploadModal(false);
                  setConversionProgress(null);
                  setPreviewImages([]);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <IoClose className="w-5 h-5" />
              </button>
            </div>

            {/* Conversion Settings */}
            <div className="mb-4">
              <button
                onClick={() => setShowConversionSettings(!showConversionSettings)}
                className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                <FaFilter className="w-3 h-3" />
                Conversion Settings
              </button>
              
              {showConversionSettings && (
                <div className="mt-3 p-4 bg-gray-50 rounded-lg space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Quality ({Math.round(conversionOptions.quality * 100)}%)
                    </label>
                    <input
                      type="range"
                      min="50"
                      max="100"
                      value={conversionOptions.quality * 100}
                      onChange={(e) => setConversionOptions(prev => ({
                        ...prev,
                        quality: parseInt(e.target.value) / 100
                      }))}
                      className="w-full"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Max Width (px)
                    </label>
                    <input
                      type="number"
                      value={conversionOptions.maxWidth}
                      onChange={(e) => setConversionOptions(prev => ({
                        ...prev,
                        maxWidth: parseInt(e.target.value) || 1920
                      }))}
                      className="w-full px-3 py-1 border border-gray-300 rounded-md"
                    />
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="generateThumbnail"
                      checked={conversionOptions.generateThumbnail}
                      onChange={(e) => setConversionOptions(prev => ({
                        ...prev,
                        generateThumbnail: e.target.checked
                      }))}
                      className="rounded"
                    />
                    <label htmlFor="generateThumbnail" className="text-sm text-gray-700">
                      Generate thumbnails
                    </label>
                  </div>
                </div>
              )}
            </div>

            {!conversionProgress ? (
              <div
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}
              >
                <FaUpload className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-600 mb-2">Click to upload or drag and drop</p>
                <p className="text-sm text-gray-500 mb-1">PNG, JPG, GIF up to 10MB</p>
                <p className="text-xs text-green-600 font-medium">
                  ✓ Images will be automatically converted to WebP format
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="text-center">
                  <FaSpinner className="w-8 h-8 text-blue-600 animate-spin mx-auto mb-3" />
                  <p className="text-gray-700 font-medium">Converting images to WebP...</p>
                  <p className="text-sm text-gray-600 mt-1">
                    {conversionProgress.current} of {conversionProgress.total} images
                  </p>
                </div>
                
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${conversionProgress.percentage}%` }}
                  />
                </div>
              </div>
            )}

            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*"
              onChange={handleFileSelect}
              className="hidden"
            />

            {!supportsWebP() && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <p className="text-sm text-yellow-800">
                  ⚠️ Your browser doesn't support WebP conversion. Images will be uploaded as-is.
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Image Details Modal */}
      {selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Image Details</h3>
              <button
                onClick={() => setSelectedImage(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <IoClose className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              <img
                src={selectedImage.url}
                alt={selectedImage.alt || selectedImage.title}
                className="w-full h-64 object-cover rounded-lg"
              />
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    value={selectedImage.title}
                    onChange={(e) => setSelectedImage(prev => ({ ...prev, title: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={selectedImage.category}
                    onChange={(e) => setSelectedImage(prev => ({ ...prev, category: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    {galleryCategories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={selectedImage.description}
                  onChange={(e) => setSelectedImage(prev => ({ ...prev, description: e.target.value }))}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setSelectedImage(null)}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    onUpdateImage(selectedImage.id, {
                      title: selectedImage.title,
                      category: selectedImage.category,
                      description: selectedImage.description
                    });
                    setSelectedImage(null);
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                >
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleryManager;